import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Eye, EyeOff, Plane } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Login({ role }) {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(role === "admin" ? "admin@sparkholidays.com" : "exec@sparkholidays.com");
  const [password, setPassword] = useState("123456");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ company_name: "Spark Holidays", logo: null });

  useEffect(() => {
    api.get("/settings/public").then(r => setBrand(r.data)).catch(()=>{});
    if (user) nav(`/${user.role}/dashboard`);
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password, role);
      toast.success(`Welcome back, ${u.name}!`);
      nav(`/${u.role}/dashboard`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Login failed");
    } finally { setLoading(false); }
  };

  const otherRole = role === "admin" ? "executive" : "admin";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <Toaster position="top-right" richColors/>
      {/* Hero — left column at lg+, hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 login-hero relative">
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <Plane size={18}/>
            <span className="text-sm uppercase tracking-widest font-medium opacity-90">Spark Holidays · Travel CRM</span>
          </div>
          <div>
            <h2 className="text-4xl sm:text-5xl font-semibold leading-tight mb-3" style={{fontFamily:'Outfit'}}>
              Turn every inquiry<br/>into a memorable journey.
            </h2>
            <p className="text-sm opacity-80 max-w-md">
              Manage leads, follow-ups, invoices and teams — all in one enterprise-ready CRM built for travel agencies.
            </p>
          </div>
        </div>
      </div>

      {/* Form — right column at lg+, full width on mobile */}
      <div className="flex-1 min-h-screen flex flex-col justify-center items-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            {brand.logo ? (
              <img src={brand.logo} alt="logo" className="h-12 w-12 rounded-xl object-contain bg-white p-1 border border-gray-100"/>
            ) : (
              <div className="h-11 w-11 rounded-xl bg-[#4f87ff] flex items-center justify-center text-white font-bold" style={{fontFamily:'Outfit'}}>S</div>
            )}
            <div>
              <div className="text-lg font-semibold text-gray-900" style={{fontFamily:'Outfit'}}>{brand.company_name}</div>
              <div className="text-[11px] uppercase tracking-widest text-gray-400">CRM</div>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-2" style={{fontFamily:'Outfit'}}>
            {role === "admin" ? "Admin Sign In" : "Executive Sign In"}
          </h1>
          <p className="text-sm text-gray-500 mb-10">Welcome back. Please enter your credentials to continue.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 block">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="email"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
                className="w-full h-11 px-4 rounded-md bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#4f87ff] focus:ring-2 focus:ring-[#EEF2FF] outline-none text-sm transition" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 block">Password</label>
              <div className="relative">
                <input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="current-password"
                  autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  className="w-full h-11 px-4 pr-12 rounded-md bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#4f87ff] focus:ring-2 focus:ring-[#EEF2FF] outline-none text-sm transition" />
                <button type="button" onClick={()=>setShowPw(s=>!s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-11 rounded-md bg-[#4f87ff] hover:bg-[#3a6fe0] text-white font-medium text-sm transition disabled:opacity-60">
              {loading ? "Signing in…" : `Sign in as ${role === "admin" ? "Admin" : "Executive"}`}
            </button>
          </form>

          <div className="mt-6 text-xs text-gray-500">
            <Link to={`/${otherRole}/login`} className="hover:text-[#4f87ff] underline-offset-2 hover:underline">
              Login as {otherRole}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
